import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon, Logout03Icon } from "@hugeicons/core-free-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/stores/authStore";

interface NavItem {
  to: string;
  label: string;
}

interface NavGroup {
  label: string;
  /** 단일 목적지는 드롭다운 없이 바로 링크. 클릭 한 번 아끼는 게 맞다. */
  to?: string;
  items?: NavItem[];
}

/**
 * 메뉴가 늘어날수록 평평한 나열은 읽히지 않는다. 성격별 그룹을 상수로 두고
 * 새 화면은 해당 그룹에 한 줄 추가하는 식으로만 늘린다.
 */
const NAV_GROUPS: NavGroup[] = [
  { label: "업체 관리", to: "/companies" },
  {
    label: "분석 · 정산",
    items: [
      { to: "/order-analytics", label: "배차 업체별 오더 분석" },
      { to: "/sms-billing", label: "SMS 월별 청구" },
    ],
  },
  {
    label: "권한 · 계정",
    items: [
      { to: "/role-permissions", label: "권한·역할 관리" },
      { to: "/accounts", label: "계정 도구" },
    ],
  },
  {
    label: "시스템",
    items: [
      { to: "/brands", label: "브랜드 관리" },
      { to: "/custom-addresses", label: "보완 주소 관리" },
      { to: "/programs", label: "프로그램 배포" },
    ],
  },
];

const navItemClass = (isActive: boolean) =>
  cn(
    "rounded-md px-3 py-2 text-sm transition-colors",
    isActive
      ? "bg-gray-100 font-medium text-gray-900"
      : "text-gray-600 hover:bg-gray-50",
  );

export const Header = () => {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /** 드롭다운 그룹은 하위 경로 중 하나라도 열려 있으면 활성 표시. */
  const activeGroupLabel = useMemo(() => {
    const hit = NAV_GROUPS.find((g) =>
      g.items?.some((i) => pathname.startsWith(i.to)),
    );
    return hit?.label ?? null;
  }, [pathname]);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white">
      <div className="flex h-14 items-center px-4 md:h-16">
        {/* 로고 */}
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-gray-900">ULA Admin</span>
        </div>

        {/* 네비게이션 */}
        <nav className="ml-6 hidden items-center gap-1 md:flex">
          {NAV_GROUPS.map((group) =>
            group.to ? (
              <NavLink
                key={group.label}
                to={group.to}
                className={({ isActive }) => navItemClass(isActive)}
              >
                {group.label}
              </NavLink>
            ) : (
              <DropdownMenu key={group.label}>
                <DropdownMenuTrigger
                  className={cn(
                    navItemClass(activeGroupLabel === group.label),
                    "flex items-center gap-1 outline-none",
                  )}
                >
                  {group.label}
                  <HugeiconsIcon
                    icon={ArrowDown01Icon}
                    size={12}
                    className="text-gray-400"
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[160px]">
                  {group.items?.map((item) => (
                    <DropdownMenuItem
                      key={item.to}
                      onClick={() => navigate(item.to)}
                      className={cn(
                        "px-2 py-1.5 text-xs",
                        pathname.startsWith(item.to) &&
                          "font-medium text-blue-600",
                      )}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          )}
        </nav>

        {/* 우측 사용자 메뉴 */}
        <div ref={menuRef} className="relative ml-auto">
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-gray-50"
          >
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">
                {user?.userName ?? "관리자"}
              </p>
              <p className="text-xs text-gray-500">{user?.companyName ?? ""}</p>
            </div>
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={14}
              className={cn(
                "text-gray-400 transition-transform",
                menuOpen && "rotate-180",
              )}
            />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-1 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-gray-50"
              >
                <HugeiconsIcon icon={Logout03Icon} size={14} />
                로그아웃
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
